import { Add, Reorder, ViewStream } from '@mui/icons-material';
import { Button, Chip, Fab, IconButton, Typography } from '@mui/material';
import { Draggable } from '@prismamedia/one-components';
import { SectionFragment } from '__generated__/queries-newsletter';
import { BasicDroppable } from 'components/BasicDroppable';
import {
  actions,
  CampaignSectionAction,
} from 'pages/CampaignPage/CampaignEditPage/ContentPage/reducer';
import { useDisplayArticles } from 'pages/CampaignPage/CampaignEditPage/ContentPage/utils/useDisplayArticles';
import { Dispatch, FC, memo, useEffect, useRef, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { usePrevious } from 'utils/usePrevious';
import { Section } from './Section';
import { useStyles } from './styles';

interface SectionsProps {
  sections: EnrichedCampaignSection[];
  newsletterSectionConfig: string | null;
  dispatch: Dispatch<CampaignSectionAction>;
}

export interface NewsletterSectionConfig {
  sectionResourceCount: number;
}

export type EnrichedCampaignSection = {
  maxBlocksAmount?: number;
} & SectionFragment;

export const Sections: FC<SectionsProps> = memo(
  ({ sections = [], newsletterSectionConfig, dispatch }) => {
    const classes = useStyles();
    const { isMobile } = useDisplayArticles();
    const [disableDrag, setDisableDrag] = useState<boolean>(false);
    const [compactView, setCompactView] = useState<boolean>(false);
    const rootEl = useRef<HTMLDivElement>(null);

    /**
     * The purpose of this effect is to detect when the dialog opened
     * by the MultiMediaManager or the mobile drawers are added or removed to the DOM in order
     * to disable the drag & drop of Sections and Blocks
     */
    useEffect(() => {
      const observer = new MutationObserver((mutations: MutationRecord[]) => {
        const isMuiDialogRoot = (nodes: NodeList): boolean =>
          Array.from(nodes).some(
            (node: any) =>
              node.className === 'MuiDialog-root' ||
              node.className === 'MuiDrawer-root MuiDrawer-modal',
          );
        mutations.forEach((mutation: MutationRecord) => {
          if (
            mutation.addedNodes.length &&
            isMuiDialogRoot(mutation.addedNodes)
          ) {
            setDisableDrag(true);
          }
          if (
            mutation.removedNodes.length &&
            isMuiDialogRoot(mutation.removedNodes)
          ) {
            setDisableDrag(false);
          }
        });
      });

      const targetNode = document.body;
      observer.observe(targetNode, {
        childList: true,
      });
      return () => {
        observer.disconnect();
      };
    }, []);

    /**
     * Scroll to the bottom of the Sections's div when adding a new section
     */
    const prevSectionsLength = usePrevious(sections.length);

    useEffect(() => {
      if (
        rootEl.current &&
        rootEl.current.firstElementChild &&
        prevSectionsLength !== sections.length
      ) {
        // eslint-disable-next-line immutable/no-mutation
        rootEl.current.firstElementChild.scrollTop =
          rootEl.current.firstElementChild.scrollHeight;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections.length]);

    return (
      <div className={classes.root} ref={rootEl}>
        {!isMobile && (
          <div className={classes.toolbar}>
            <div>
              <Typography variant="body2">Quantité de sections :</Typography>
              <Chip label={sections.length} size="small" />
            </div>
            <IconButton
              onClick={() => setCompactView(!compactView)}
              title={`Passer en vue ${compactView ? 'édition' : 'tri'}`}
              size="large"
            >
              {compactView ? <ViewStream /> : <Reorder />}
            </IconButton>
          </div>
        )}
        <BasicDroppable
          droppableId="sections"
          droppableType="sections"
          droppableKey="sections"
          className={classes.sectionContainer}
        >
          {sections.map((section, i) => (
            <Draggable
              draggableId={section.id}
              key={section.id}
              index={i}
              isDragDisabled={!!newsletterSectionConfig || disableDrag}
            >
              {(dragProvided: DraggableProvided) => (
                <div
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  {...dragProvided.dragHandleProps}
                >
                  <Section
                    key={section.id}
                    section={section}
                    dispatch={dispatch}
                    disableDrag={disableDrag}
                    compactView={compactView}
                    isTemplateFromNlf={!!newsletterSectionConfig}
                    maxBlocksAmount={section.maxBlocksAmount}
                  />
                </div>
              )}
            </Draggable>
          ))}
        </BasicDroppable>
        {!isMobile && !newsletterSectionConfig && (
          <Button
            className={classes.addSectionButton}
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => dispatch(actions.addSection())}
          >
            AJOUTER UNE SECTION
          </Button>
        )}
        {isMobile && !newsletterSectionConfig && (
          <Fab
            color="primary"
            className={classes.fab}
            onClick={() => dispatch(actions.addSection())}
          >
            <Add />
          </Fab>
        )}
      </div>
    );
  },
);
